import LanguageSelector from "@/components/languageSelector/LanguageSelector";
import { TopBarMobile } from "@/components/topBarMobile/topBarMobile";
import { PspContext } from "@/config/contexts";
import { useIsTablet } from "@/config/hooks";
import { translate } from "@/config/translator";
import { faChevronLeft } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";

export default function PrivacyPolicy() {
  const { lang } = useContext(PspContext);
  const navigate = useNavigate();
  const isTablet = useIsTablet();

  return (
    <div className={`${isTablet ? "p-8" : ""} overflow-y-scroll h-screen`}>
      {isTablet ? (
        <>
          <div className="flex flex-row items-center justify-between">
            {/* BACK BUTTON */}
            <button onClick={() => navigate(-1)} className="cursor-pointer m-4">
              <FontAwesomeIcon
                icon={faChevronLeft}
                className="text-3xl text-font-secondary"
              />
            </button>

            {/* LANGUAGE SELECTOR */}
            <div className="w-60">
              <LanguageSelector />
            </div>
          </div>

          {/* PAGE TITLE */}
          <h1 className="font-bold text-3xl my-8">
            {translate("privacyPageTitle", lang)}
          </h1>
        </>
      ) : (
        <TopBarMobile header="privacyPageTitle" onBack={() => navigate(-1)} />
      )}

      <div className={isTablet ? "" : "p-6"}>
        {/* WHO WE ARE */}
        <h2 className="font-bold text-xl mt-4">
          {translate("privacyWhoWeAreTitle", lang)}
        </h2>
        <ul className="list-disc gap-2 flex flex-col">
          <li>{translate("privacyWhoWeAreController", lang)}</li>
          <li>{translate("privacyWhoWeAreContact", lang)}</li>
          <li>{translate("privacyWhoWeAreDpo", lang)}</li>
        </ul>

        {/* WHAT THIS POLICY COVERS */}
        <h2 className="font-bold text-xl mt-4">
          {translate("privacyWhatThisPolicyCoversTitle", lang)}
        </h2>
        <p>{translate("privacyWhatThisPolicyCoversBody", lang)}</p>

        {/* WHAT DATA WE PROCESS */}
        <h2 className="font-bold text-xl mt-4">
          {translate("privacyWhatDataWeProcessTitle", lang)}
        </h2>
        <ul className="list-disc gap-2 flex flex-col">
          <li>{translate("privacyWhatDataWeProcessAccountData", lang)}</li>
          <li>{translate("privacyWhatDataWeProcessBusinessData", lang)}</li>
          <li>{translate("privacyWhatDataWeProcessUploadedContent", lang)}</li>
          <li>
            {translate("privacyWhatDataWeProcessCommunicationData", lang)}
          </li>
          <li>{translate("privacyWhatDataWeProcessLogs", lang)}</li>
          <li>{translate("privacyWhatDataWeProcessImportedData", lang)}</li>

          {/* ... categories missing here ... */}
        </ul>

        {/* WHERE WE GOT YOUR DATA */}
        <h2 className="font-bold text-xl mt-4">
          {translate("privacyWhereWeGotYourDataTitle", lang)}
        </h2>
        <ul className="list-disc gap-2 flex flex-col">
          <li>{translate("privacyWhereWeGotYourDataDirectly", lang)}</li>
          <li>{translate("privacyWhereWeGotYourDataIndirectly", lang)}</li>
        </ul>
        <p className="mt-4">
          {translate("privacyWhereWeGotYourDataSeeSection13", lang)}
        </p>

        {/* WHY WE PROCESS DATA & LEGAL BASES */}
        <h2 className="font-bold text-xl mt-4">
          {translate("privacyWhyWeProcessDataTitle", lang)}
        </h2>
        <ul className="list-disc gap-2 flex flex-col">
          <li>{translate("privacyWhyWeProcessDataHeader", lang)}</li>
          <li>{translate("privacyWhyWeProcessDataProvideService", lang)}</li>
          <li>{translate("privacyWhyWeProcessDataCompliance", lang)}</li>
          <li>{translate("privacyWhyWeProcessDataSecurity", lang)}</li>
          <li>{translate("privacyWhyWeProcessDataImprovement", lang)}</li>
          <li>{translate("privacyWhyWeProcessDataDirectComms", lang)}</li>
          <li>{translate("privacyLegitimateInterestsExplained", lang)}</li>
          <li>{translate("privacySpecialCategoryNote", lang)}</li>
        </ul>

        {/* WHO RECEIVES DATA */}
        <h2 className="font-bold text-xl mt-4">
          {translate("privacyWhoReceivesDataTitle", lang)}
        </h2>
        <ul className="list-disc gap-2 flex flex-col">
          <li>{translate("privacyWhoReceivesDataInternal", lang)}</li>
          <li>{translate("privacyWhoReceivesDataProcessors", lang)}</li>
          <li>{translate("privacyWhoReceivesDataAuthorities", lang)}</li>
        </ul>

        {/* INTERNATIONAL TRANSFERS */}
        <h2 className="font-bold text-xl mt-4">
          {translate("privacyInternationalTransfersTitle", lang)}
        </h2>
        <p>{translate("privacyInternationalTransfersBody", lang)}</p>

        {/* HOW LONG WE KEEP DATA */}
        <h2 className="font-bold text-xl mt-4">
          {translate("privacyRetentionTitle", lang)}
        </h2>
        <p>{translate("privacyRetentionBody", lang)}</p>

        {/* YOUR RIGHTS */}
        <h2 className="font-bold text-xl mt-4">
          {translate("privacyYourRightsTitle", lang)}
        </h2>
        <p>{translate("privacyYourRightsBody", lang)}</p>

        {/* AUTOMATED DECISION-MAKING */}
        <h2 className="font-bold text-xl mt-4">
          {translate("privacyAutomatedDecisionMakingTitle", lang)}
        </h2>
        <p>{translate("privacyAutomatedDecisionMakingBody", lang)}</p>

        {/* DO YOU HAVE TO PROVIDE DATA? */}
        <h2 className="font-bold text-xl mt-4">
          {translate("privacyDoYouHaveToProvideDataTitle", lang)}
        </h2>
        <p>{translate("privacyDoYouHaveToProvideDataBody", lang)}</p>

        {/* SECURITY */}
        <h2 className="font-bold text-xl mt-4">
          {translate("privacySecurityTitle", lang)}
        </h2>
        <p>{translate("privacySecurityBody", lang)}</p>

        {/* IF YOUR DATA WAS IMPORTED */}
        <h2 className="font-bold text-xl mt-4">
          {translate("privacyImportedDataTitle", lang)}
        </h2>
        <p>{translate("privacyImportedDataBody", lang)}</p>

        {/* CHANGES TO THIS NOTICE */}
        <h2 className="font-bold text-xl mt-4">
          {translate("privacyChangesTitle", lang)}
        </h2>
        <p>{translate("privacyChangesBody", lang)}</p>
      </div>
    </div>
  );
}
